import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { logger } from '@zep/core-logger';

import { type QuizSetDetailsRes } from '@/api';
import { useUser } from '@/lib/auth';

import { useQuizCopy } from './useQuizCopy';

interface UseDuplicateLockedHandlerParams {
  quizId: string;
  quizData: QuizSetDetailsRes;
}

/**
 * useDuplicateLockedHandler - 잠긴 퀴즈 복사 핸들러
 *
 * @description
 * 잠긴 퀴즈 복사 버튼 클릭 시 동작을 처리합니다.
 * - 비로그인 사용자: 로그인 페이지로 리다이렉트 (콜백 URL 포함)
 * - 로그인 사용자: 잠긴 상태로 퀴즈 복사 진행
 */
export const useDuplicateLockedHandler = ({
  quizId,
  quizData,
}: UseDuplicateLockedHandlerParams) => {
  const router = useRouter();
  const { user } = useUser();
  const quizCopy = useQuizCopy();

  return useCallback(() => {
    logger.track('enter_duplicate_locked', {
      location: 'detail',
      spaceId: quizData.id,
      title: quizData.title,
    });

    // 비로그인 상태면 로그인 페이지로 리다이렉트 (로그인 후 현재 페이지로 돌아옴)
    if (!user) {
      const currentUrl = `/quiz/${quizId}`;
      void router.push({
        pathname: '/login',
        query: { callbackUrl: currentUrl },
      });
      return;
    }

    // 로그인 상태면 복사 진행
    quizCopy.mutate({
      quizId: quizData.id,
      title: quizData.title,
      isLocked: true,
    });
  }, [router, user, quizCopy, quizId, quizData]);
};
